/**
 * Reusable filter bar used by the Runs and Uploads list pages.
 *
 * Designed to be page-agnostic — the consumer supplies the lists of valid
 * status/trigger values for the entity it's filtering, plus a search
 * placeholder string. Filter state itself is owned by the page so a
 * navigation back to the page resets to defaults (which matches the
 * "client-side, no URL persistence yet" decision in lib/filters.ts).
 */

import type { FilterState, SortMode } from "../lib/filters";
import { EMPTY_FILTERS, isFiltersEmpty } from "../lib/filters";

export interface FilterOption {
  value: string;
  label: string;
}

export interface FilterBarProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  /** Valid status values for this entity, with display labels. */
  statuses: FilterOption[];
  /** Valid trigger values for this entity, with display labels. */
  triggers: FilterOption[];
  /** Canvas env options; renders an env pill row when provided. History
   *  lists show BOTH envs by default — these pills narrow on demand. */
  envs?: FilterOption[];
  searchPlaceholder?: string;
  /** Total items in the page (pre-filter). */
  totalCount: number;
  /** Items remaining after filters. */
  filteredCount: number;
}

const SORT_OPTIONS: { value: SortMode; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "duration-desc", label: "Longest duration" },
  { value: "duration-asc", label: "Shortest duration" },
  { value: "status", label: "By status" },
];

export function FilterBar({
  value,
  onChange,
  statuses,
  triggers,
  envs,
  searchPlaceholder = "Search…",
  totalCount,
  filteredCount,
}: FilterBarProps) {
  const dirty = !isFiltersEmpty(value);

  function toggleStatus(s: string) {
    const set = new Set(value.statuses);
    if (set.has(s)) set.delete(s);
    else set.add(s);
    onChange({ ...value, statuses: [...set] });
  }

  function toggleTrigger(t: string) {
    const set = new Set(value.triggers);
    if (set.has(t)) set.delete(t);
    else set.add(t);
    onChange({ ...value, triggers: [...set] });
  }

  function toggleEnv(e: string) {
    const set = new Set(value.envs);
    if (set.has(e)) set.delete(e);
    else set.add(e);
    onChange({ ...value, envs: [...set] });
  }

  return (
    <div className="filter-bar">
      <div className="filter-row filter-row-primary">
        <input
          type="text"
          className="filter-search"
          placeholder={searchPlaceholder}
          value={value.search}
          onChange={(e) => onChange({ ...value, search: e.target.value })}
          aria-label="Search"
        />

        <select
          className="filter-sort"
          value={value.sort}
          onChange={(e) =>
            onChange({ ...value, sort: e.target.value as SortMode })
          }
          aria-label="Sort by"
        >
          {SORT_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <div className="filter-date-range" role="group" aria-label="Date range">
          <input
            type="date"
            className="filter-date"
            value={value.fromDate ?? ""}
            onChange={(e) =>
              onChange({ ...value, fromDate: e.target.value || null })
            }
            aria-label="From date"
          />
          <span className="filter-date-sep">→</span>
          <input
            type="date"
            className="filter-date"
            value={value.toDate ?? ""}
            onChange={(e) =>
              onChange({ ...value, toDate: e.target.value || null })
            }
            aria-label="To date"
          />
        </div>

        <div className="filter-count">
          {dirty ? (
            <>
              <strong>{filteredCount}</strong> of {totalCount}
            </>
          ) : (
            <>
              <strong>{totalCount}</strong> total
            </>
          )}
        </div>

        {dirty && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => onChange(EMPTY_FILTERS)}
          >
            Clear
          </button>
        )}
      </div>

      <div className="filter-row filter-row-pills">
        <span className="filter-label">Status</span>
        {statuses.map((s) => (
          <PillToggle
            key={s.value}
            label={s.label}
            active={value.statuses.includes(s.value)}
            onClick={() => toggleStatus(s.value)}
          />
        ))}
      </div>

      <div className="filter-row filter-row-pills">
        <span className="filter-label">Trigger</span>
        {triggers.map((t) => (
          <PillToggle
            key={t.value}
            label={t.label}
            active={value.triggers.includes(t.value)}
            onClick={() => toggleTrigger(t.value)}
          />
        ))}
      </div>

      {envs && envs.length > 0 && (
        <div className="filter-row filter-row-pills">
          <span className="filter-label">Canvas env</span>
          {envs.map((e) => (
            <PillToggle
              key={e.value}
              label={e.label}
              active={value.envs.includes(e.value)}
              onClick={() => toggleEnv(e.value)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PillToggle({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={"filter-pill" + (active ? " filter-pill-active" : "")}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}
